import IngestionStatus from "datasets/pages/tables/types/enums/ingest-status";
import type { IngestResponse } from "datasets/pages/tables/types/interfaces/ingest";
import type { SWRResponse } from "swr";
import { useFetcher } from "~/hooks/useFetcher";

const { VITE_CATALOG_API } = import.meta.env;

// Bounded cadence while a load is in flight. The load emits no percent or live
// row count (see design D5), so this is the only "still going" signal we poll.
const POLL_INTERVAL_MS = 3000;

// draft/pending/started are non-terminal; completed/failed are terminal and
// never change again, so a row that reaches them stops polling.
const TERMINAL_STATUSES: ReadonlySet<string> = new Set([
  IngestionStatus.COMPLETED,
  IngestionStatus.FAILED,
]);

// Polls a single catalog ingest job by id, independent of any active agent turn
// (SWR refreshes regardless of run state). Refreshes on a bounded interval while
// the load is non-terminal and stops once it reaches completed/failed. A `null`
// id skips the request entirely — nothing is focused.
export const useIngestStatus = (jobId: string | null): SWRResponse<IngestResponse> =>
  useFetcher<IngestResponse>(jobId ? `${VITE_CATALOG_API}/ingests/${jobId}` : null, {
    refreshInterval: (latest?: IngestResponse) =>
      latest && TERMINAL_STATUSES.has(latest.status) ? 0 : POLL_INTERVAL_MS,
  });
